/**
 * src/lib/chunkReloadBreadcrumb.js
 *
 * sessionStorage breadcrumb for chunk-reload tracking (TICKET-019 Phase 1).
 *
 * When ErrorBoundary auto-reloads on a stale-chunk error, it writes a breadcrumb
 * here BEFORE calling window.location.reload(). On the next boot, main.jsx (or
 * AppShell) reads the breadcrumb to emit a `chunk_reload_completed` event with
 * `time_to_recover_ms`, then clears it.
 *
 * sessionStorage is the right scope: per-tab, survives reloads, dies on tab close.
 * (LocalStorage would falsely "complete" reloads in unrelated tabs.)
 *
 * Phase 2 will additionally read `from_view` from the breadcrumb to restore the
 * user's previous view after the reload, instead of dumping them on Focus.
 */

const KEY = 'chunk_reload_breadcrumb';

export function writeBreadcrumb({ from_view, error_msg }) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({
      from_view: from_view || null,
      error_msg: (error_msg || '').slice(0, 500),
      ts: Date.now(),
    }));
  } catch {
    // sessionStorage can be disabled (private mode, certain enterprise policies).
    // Failing here would mask the real chunk-reload, so swallow.
  }
}

export function readAndClearBreadcrumb() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    return { ...parsed, time_to_recover_ms: Date.now() - parsed.ts };
  } catch {
    return null;
  }
}
